import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Mail, Phone, Calendar, LogOut, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminVolunteers = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    fetchApplications(token);
  }, [navigate]);

  const fetchApplications = async (token) => {
    try {
      const response = await axios.get(`${API}/volunteers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data.applications || []);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-xl text-gray-600">Loading applications...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="volunteers-page-title">
                Volunteer <span className="text-[#E6B933]">Applications</span>
              </h1>
              <p className="text-lg text-gray-600">
                {applications.length} application{applications.length !== 1 ? 's' : ''} received
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/admin/partnerships">
                <Button variant="outline" className="border-[#E6B933] text-[#E6B933] hover:bg-[#E6B933]/10" data-testid="nav-partnerships">
                  <Users className="w-4 h-4 mr-2" /> Partnerships
                </Button>
              </Link>
              <Button variant="outline" onClick={handleLogout} className="text-red-500 border-red-300 hover:bg-red-50" data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            {applications.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-gray-600">No applications yet.</p>
              </Card>
            ) : (
              applications.map((app, index) => (
                <Card key={app._id || index} className="p-6 hover:shadow-lg transition-all duration-300">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{app.name}</h3>
                      <p className="text-[#E6B933] font-semibold">{app.interest}</p>
                    </div>
                    <Badge className="bg-[#E6B933] text-white">
                      {app.status || 'pending'}
                    </Badge>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Mail size={16} />
                      <a href={`mailto:${app.email}`} className="hover:text-[#E6B933]">
                        {app.email}
                      </a>
                    </div>
                    {app.phone && (
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Phone size={16} />
                        <a href={`tel:${app.phone}`} className="hover:text-[#E6B933]">
                          {app.phone}
                        </a>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 text-gray-600">
                      <Calendar size={16} />
                      <span>{new Date(app.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {app.message && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Message:</p>
                      <p className="text-gray-600">{app.message}</p>
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminVolunteers;
